import {
	Button,
	ButtonGroup,
	Dialog,
	IconButton,
	LuIcon,
	Separator,
	stopPropagation,
	Text,
	toast,
} from "@overmap-ai/blocks"
import {
	type Form,
	type FormIdentifier,
	type FormRevision,
	type FormSubmission,
	type Stored,
	toFileNameSafeString,
	type User,
} from "@overmap-ai/common"
import { FieldSchema } from "@overmap-ai/forms"
import { saveAs } from "file-saver"
import JSZip from "jszip"
import Papa from "papaparse"
import { memo, useCallback, useMemo, useState } from "react"
import type { DateRange } from "react-day-picker"

import { getFormSubmissionDataCsvExporter } from "../../csv/CSVExporter/forms"
import { modelIconMapping } from "../../mappings/icons"
import { useAppSelector } from "../../store"
import {
	selectLatestFieldValuesByFormSubmission,
	selectLatestIdentifierValuesByFormSubmission,
} from "../../store/selectors/forms"
import type { FormSubmissionData } from "../../typings"
import { formatDateRange, isDateInRange } from "../../utils/date"
import { DayPickerPopover } from "../Popovers/DayPickerPopover"
import type { ExtendableDialogProps } from "./typings"

interface ExportFormSubmissionsCsvDialogProps extends Omit<ExtendableDialogProps, "title"> {
	forms: Stored<Form>[]
	formSubmissions: Stored<FormSubmission>[]
	formRevisions: Stored<FormRevision>[]
	formIdentifiers: Stored<FormIdentifier>[]
	users: Stored<User>[]
}

export const ExportFormSubmissionsCsvDialog = memo<ExportFormSubmissionsCsvDialogProps>((props) => {
	const {
		children,
		forms,
		formSubmissions,
		formRevisions,
		formIdentifiers,
		users,
		open,
		onOpenChange,
		defaultOpen,
		modal,
		...rest
	} = props

	const [dateRange, setDateRange] = useState<DateRange | undefined>()

	const formIds = useMemo(() => new Set(forms.map((form) => form.uuid)), [forms])

	const latestFormRevisionByForm = useMemo(() => {
		const ret: Record<Form["uuid"], Stored<FormRevision> | undefined> = {}

		for (const formRevision of formRevisions) {
			const current = ret[formRevision.form]

			if (!current || current.submitted_at <= formRevision.submitted_at) {
				ret[formRevision.form] = formRevision
			}
		}

		return ret
	}, [formRevisions])

	const formIdentifiersByForm = useMemo(() => {
		const ret: Record<Form["uuid"], Stored<FormIdentifier>[]> = {}

		for (const formIdentifier of formIdentifiers) {
			if (!ret[formIdentifier.form]) ret[formIdentifier.form] = []
			ret[formIdentifier.form]!.push(formIdentifier)
		}

		return ret
	}, [formIdentifiers])

	const formSubmissionsByForm = useMemo(() => {
		const ret: Record<Form["uuid"], Stored<FormSubmission>[]> = {}

		for (const formSubmission of formSubmissions) {
			if (!ret[formSubmission.form]) ret[formSubmission.form] = []
			ret[formSubmission.form]!.push(formSubmission)
		}

		return ret
	}, [formSubmissions])

	const formIdentifierValuesByFormSubmission = useAppSelector(selectLatestIdentifierValuesByFormSubmission)

	const latestFieldValuesByFormSubmission = useAppSelector(selectLatestFieldValuesByFormSubmission)

	const filteredFormSubmissionCount = useMemo<number>(() => {
		return formSubmissions.filter(
			(formSubmission) =>
				formIds.has(formSubmission.form) && isDateInRange(formSubmission.submitted_at, dateRange),
		).length
	}, [dateRange, formSubmissions, formIds])

	const handleClearDateRange = useCallback(() => {
		setDateRange(undefined)
	}, [])

	const handleExport = useCallback(async () => {
		if (filteredFormSubmissionCount === 0) return

		try {
			const zip = new JSZip()
			const fileNameCounts: Record<string, number> = {}

			for (const form of forms) {
				const formRevision = latestFormRevisionByForm[form.uuid]
				const formIdentifiers = formIdentifiersByForm[form.uuid] ?? []
				const formSubmissions = formSubmissionsByForm[form.uuid] ?? []

				if (formSubmissions.length === 0) continue

				const formFieldSchema = FieldSchema.deserialize(formRevision?.fields ?? [])

				const formSubmissionData: FormSubmissionData[] = formSubmissions
					.filter((formSubmission) => isDateInRange(formSubmission.submitted_at, dateRange))
					.map((formSubmission) => ({
						formSubmission: formSubmission,
						formIdentifierValues: formIdentifierValuesByFormSubmission[formSubmission.uuid] ?? {},
						formSubmissionFieldValues: latestFieldValuesByFormSubmission[formSubmission.uuid],
						formSubmissionFieldValuesAttachments: [],
					}))

				const exporter = getFormSubmissionDataCsvExporter(formSubmissionData, {
					formIdentifiers,
					formFieldSchema,
					users,
				})
				const csvData = Papa.unparse(exporter.toPapaUnparseFormat())

				const count = (fileNameCounts[form.name] ?? 0) + 1
				fileNameCounts[form.name] = count
				const uniqueFileName = count > 1 ? `${form.name} (${count})` : form.name
				zip.file(toFileNameSafeString(uniqueFileName, ".csv"), csvData)
			}

			const blob = await zip.generateAsync({ type: "blob" })
			saveAs(blob, toFileNameSafeString("Form submissions", ".zip"))

			onOpenChange?.(false)
		} catch {
			toast.error({
				title: "Export failed",
				description: "The form submission CSV export could not be created.",
			})
		}
	}, [
		filteredFormSubmissionCount,
		onOpenChange,
		users,
		latestFormRevisionByForm,
		formIdentifiersByForm,
		formSubmissionsByForm,
		dateRange,
		forms,
		formIdentifierValuesByFormSubmission,
		latestFieldValuesByFormSubmission,
	])

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange} defaultOpen={defaultOpen} modal={modal}>
			{!!children && <Dialog.Trigger asChild>{children}</Dialog.Trigger>}
			<Dialog.Overlay onClick={stopPropagation} />
			<Dialog.Content className="flex flex-col gap-4" onClick={stopPropagation} {...rest}>
				<div className="flex justify-between gap-2">
					<Dialog.Title>
						<Text size="lg">Export forms</Text>
					</Dialog.Title>
					<Dialog.Close asChild>
						<IconButton variant="ghost" accentColor="base">
							<LuIcon icon={modelIconMapping.close} />
						</IconButton>
					</Dialog.Close>
				</div>

				<Separator size="full" />

				<div className="flex flex-col gap-4">
					<div className="flex flex-col gap-2">
						<Text size="sm">Filter by submitted date</Text>
						<div className="flex items-center gap-2">
							<DayPickerPopover
								mode="range"
								required={false}
								selected={dateRange}
								onSelect={setDateRange}
								variant="solid"
								align="start"
								size="sm"
							>
								<Button className="w-max" type="button" variant="soft" accentColor="base" size="sm">
									<LuIcon icon={modelIconMapping.dueDate} />
									{dateRange ? formatDateRange(dateRange) : "Date range"}
								</Button>
							</DayPickerPopover>
							{!!dateRange && (
								<IconButton
									type="button"
									variant="soft"
									accentColor="base"
									size="sm"
									onClick={handleClearDateRange}
								>
									<LuIcon icon={modelIconMapping.close} />
								</IconButton>
							)}
						</div>
						<Text size="xs" accentColor="base">
							The date range applies to every selected form in this export.
						</Text>
					</div>

					{filteredFormSubmissionCount === 0 && (
						<Text size="sm" accentColor="base">
							No selected forms have submissions in the current date range.
						</Text>
					)}
				</div>

				<ButtonGroup className="flex justify-end gap-2" size="sm">
					<Dialog.Close asChild>
						<Button type="button" variant="soft" accentColor="base">
							Cancel
						</Button>
					</Dialog.Close>
					<Button
						type="button"
						variant="surface"
						accentColor="primary"
						disabled={filteredFormSubmissionCount === 0}
						onClick={handleExport}
					>
						<LuIcon icon={modelIconMapping.download} />
						Export ZIP
					</Button>
				</ButtonGroup>
			</Dialog.Content>
		</Dialog.Root>
	)
})

ExportFormSubmissionsCsvDialog.displayName = "ExportFormSubmissionsCsvDialog"
